/**
 * Video Script Service
 *
 * A dedicated service for video script generation.
 * - Stage 1 (analysis/clarification): Uses Perplexity sonar-reasoning-pro
 * - Stage 2 (script generation): Uses Claude 4.5 Sonnet/Opus
 *
 * This service is stateless - each call is a fresh, independent request.
 */

import type { TokenUsageMetadata } from '../components/GlobalChatSystem/types';
import {
  buildVideoScriptStage2UserMessage,
} from './agenticWorkflows/constants';
import {
  TESTING_MODE,
  ENABLE_EXTENDED_THINKING,
  getThinkingConfig,
  CLAUDE_VIDEO_SCRIPT_STAGE2_MODEL,
  CLAUDE_VIDEO_SCRIPT_FALLBACK,
  PERPLEXITY_VIDEO_SCRIPT_STAGE1_MODEL,
  PERPLEXITY_VIDEO_SCRIPT_STAGE1_REASONING_EFFORT,
} from '../config/agenticWorkflowConfig';
import { runPerplexityResearch } from './perplexityClient';
import { knownAccountHint } from './accountHint';
import { SERVER_METER_HEADERS } from './proxyMeter';
import { proxyAuthHeaders } from './proxyAuth';
import { sanitizeProviderNames } from '../config/providerCodeNames';

/** Ledger-row summary for the proxy's server meter (slice 2, 2026-09-15). */
const VIDEO_SCRIPT_METER_SUMMARY = 'video script';

// Get the Firebase proxy URL for Anthropic API calls (to avoid CORS)
function getAnthropicProxyUrl(): string {
  const override = (process.env.REACT_APP_ANTHROPIC_PROXY_URL || '').trim();
  if (override) return override;
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/anthropicProxy`;
}

// ============================================================================
// Types
// ============================================================================

export interface VideoScriptAnalysisParams {
  /** The source content to analyze (research report, formatted content, etc.) */
  sourceContent: string;
  /** The title of the source content */
  sourceTitle: string;
  /** The type of source content */
  sourceType: 'research' | 'formatted' | 'translated' | 'video_script' | 'document_analysis' | 'document_analysis_failed';
  /** Optional abort signal for cancellation */
  signal?: AbortSignal;
  /** Called for each text chunk received from the stream */
  onChunk?: (chunk: string, accumulated: string) => void;
  /** Called when streaming is complete with text and token usage */
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  /** Called on error */
  onError?: (error: Error) => void;
}

export interface VideoScriptAnalysisResult {
  text: string;
  raw?: unknown;
  /** Token usage metadata for display in the UI */
  tokenUsage?: TokenUsageMetadata;
}

// ============================================================================
// Stage 1: Analysis and Clarification
// ============================================================================

/**
 * Build the user message for Stage 1 analysis.
 */
function buildStage1UserMessage(params: VideoScriptAnalysisParams): string {
  const sourceTypeLabel = {
    research: 'deep research report',
    formatted: 'formatted report',
    translated: 'translated document (Spanish)',
    video_script: 'video script',
    document_analysis: 'analyzed document',
    document_analysis_failed: 'document request (analysis fallback)',
  }[params.sourceType];

  return `Please analyze the following ${sourceTypeLabel} titled "${params.sourceTitle}" and determine if you have enough information to write a compelling video script for an immigration attorney's HeyGen avatar.

---
SOURCE CONTENT:
${params.sourceContent}
---

Based on this content, please follow the analysis steps and provide your response in the exact format specified in your instructions.`;
}

/**
 * Run Stage 1 analysis using Perplexity sonar-reasoning-pro with high reasoning effort.
 * Analyzes the source content and determines if clarifying questions are needed.
 *
 * Following the deep research workflow pattern, this uses non-streaming Perplexity API
 * with reasoning = { effort: 'high' } for maximum quality analysis.
 */
export async function runVideoScriptAnalysis(params: VideoScriptAnalysisParams): Promise<VideoScriptAnalysisResult> {
  const userMessage = buildStage1UserMessage(params);

  console.log(`[VideoScript] Stage 1 Analysis - Model: ${PERPLEXITY_VIDEO_SCRIPT_STAGE1_MODEL}, Reasoning: ${PERPLEXITY_VIDEO_SCRIPT_STAGE1_REASONING_EFFORT}`);

  try {
    const result = await runPerplexityResearch({
      query: userMessage,
      promptId: 'video_script_stage1',
      model: PERPLEXITY_VIDEO_SCRIPT_STAGE1_MODEL,
      reasoningEffort: PERPLEXITY_VIDEO_SCRIPT_STAGE1_REASONING_EFFORT,
      signal: params.signal,
    });

    const finalText = result.text;
    const tokenUsage = result.tokenUsage;

    console.log(`[VideoScript] Stage 1 Analysis complete - ${finalText.length} chars`);

    params.onComplete?.(finalText, tokenUsage);

    return { text: finalText, tokenUsage };
  } catch (error) {
    params.onError?.(error as Error);
    throw error;
  }
}

/**
 * Format video script API errors for user-friendly display.
 * Uses code names instead of provider names (Apex instead of Anthropic/Claude).
 */
export function formatVideoScriptError(err: unknown): string {
  const e = err as Record<string, unknown> | null;
  if (!e) return 'unknown error';
  const status = e.status ? `status ${e.status}` : '';
  const code = e.code ? `code ${e.code}` : '';
  // Sanitize provider names from the error message
  const rawMessage = typeof e.message === 'string' ? e.message : 'request failed';
  const message = sanitizeProviderNames(rawMessage);
  const parts = [status, code].filter(Boolean).join(', ');
  return parts ? `${message} (${parts})` : message;
}

// ============================================================================
// Stage 2: Script Generation
// ============================================================================

export interface VideoScriptGenerationParams {
  /** The source content to base the script on */
  sourceContent: string;
  /** The title of the source content */
  sourceTitle: string;
  /** Stage 1 summary */
  stage1Summary: {
    topic: string;
    targetAudience: string;
    emotionalState: string;
    platformAndLength: string;
    primaryGoalCta: string;
  };
  /** User clarifications (if any) */
  userClarifications: Array<{ question: string; answer: string }>;
  /** Optional abort signal for cancellation */
  signal?: AbortSignal;
  /** Called for each text chunk received from the stream */
  onChunk?: (chunk: string, accumulated: string) => void;
  /** Called when streaming is complete with text and token usage */
  onComplete?: (fullText: string, tokenUsage?: TokenUsageMetadata) => void;
  /** Called on error */
  onError?: (error: Error) => void;
}

export interface VideoScriptGenerationResult {
  text: string;
  raw?: unknown;
  /** Token usage metadata for display in the UI */
  tokenUsage?: TokenUsageMetadata;
}

/**
 * Run Stage 2 script generation using Claude 4.5 Sonnet with streaming.
 * Generates the complete video script based on Stage 1 analysis and user clarifications.
 */
export async function runVideoScriptGeneration(params: VideoScriptGenerationParams): Promise<VideoScriptGenerationResult> {
  // Build the user message with Stage 1 context
  const userMessage = buildVideoScriptStage2UserMessage(
    params.sourceContent,
    params.sourceTitle,
    params.stage1Summary,
    params.userClarifications
  );

  // Get thinking config based on testing mode
  const thinkingConfig = getThinkingConfig();

  // Build request body — system prompt resolved server-side via promptId
  const makeBody = (model: string) => {
    const body: Record<string, unknown> = {
      model,
      max_tokens: ENABLE_EXTENDED_THINKING ? 32000 : 8192,
      stream: true,
      // Request usage data in streaming response — without this, OpenRouter doesn't include
      // token counts in SSE chunks, causing $0 usage recording.
      stream_options: { include_usage: true },
      promptId: 'video_script_stage2',
      messages: [
        {
          role: 'user',
          content: userMessage,
        },
      ],
    };
    // Add extended thinking if enabled
    if (thinkingConfig) {
      body.thinking = thinkingConfig;
    }
    return body;
  };

  console.log(`[VideoScript] Stage 2 Generation - Model: ${CLAUDE_VIDEO_SCRIPT_STAGE2_MODEL}, Extended thinking: ${ENABLE_EXTENDED_THINKING ? 'ENABLED' : 'DISABLED'}`);

  const modelsToTry = Array.from(
    new Set([CLAUDE_VIDEO_SCRIPT_STAGE2_MODEL, CLAUDE_VIDEO_SCRIPT_FALLBACK].filter(Boolean))
  );

  // Helper to delay for exponential backoff
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  // Send request with automatic retry for transient errors
  const sendRequestWithRetry = async (model: string, maxRetries = 3): Promise<Response> => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        const backoffMs = Math.pow(2, attempt - 1) * 1000;
        console.log(`[VideoScript] Retrying after ${backoffMs}ms (attempt ${attempt + 1}/${maxRetries})`);
        await delay(backoffMs);
      }

      try {
        // Use Firebase proxy to avoid CORS issues
        const proxyUrl = getAnthropicProxyUrl();
        const accountHint = knownAccountHint();
        const response = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // API key is handled by the proxy server — and so is the wallet
            // debit (server meter, see ./proxyMeter).
            ...SERVER_METER_HEADERS,
            ...(accountHint ? { 'X-Account-Id': accountHint } : {}),
            // Who is calling — the signed-in user's ID token (security slice 6, ./proxyAuth).
            ...(await proxyAuthHeaders()),
          },
          body: JSON.stringify({ ...makeBody(model), meterSummary: VIDEO_SCRIPT_METER_SUMMARY }),
          signal: params.signal,
        });

        // If successful or non-retryable error, return immediately
        if (response.ok || (response.status !== 500 && response.status !== 502 && response.status !== 503 && response.status !== 429)) {
          return response;
        }

        console.log(`[VideoScript] Transient error ${response.status}, will retry`);

        if (attempt === maxRetries - 1) {
          return response;
        }
      } catch (fetchErr) {
        lastError = fetchErr as Error;
        if (attempt === maxRetries - 1) {
          throw lastError;
        }
      }
    }

    throw lastError || new Error('Request failed after retries');
  };

  let response: Response | null = null;
  let modelUsed = modelsToTry[0];

  try {
    let lastErr: unknown;
    for (const model of modelsToTry) {
      modelUsed = model;
      try {
        response = await sendRequestWithRetry(model);
      } catch (fetchErr) {
        console.error('[VideoScript] Fetch error after retries:', fetchErr);
        lastErr = fetchErr;
        continue;
      }

      if (!response.ok) {
        let errorData: Record<string, unknown> = {};
        try {
          errorData = await response.json();
        } catch {
          // Ignore JSON parse errors
        }
        const message = (errorData?.error as Record<string, unknown>)?.message || `Apex request failed (${response.status})`;
        const err: Error & { status?: number; code?: string } = new Error(String(message));
        err.status = response.status;
        err.code = String((errorData?.error as Record<string, unknown>)?.type || `HTTP_${response.status}`);

        const modelErr = (err.code || '').toLowerCase().includes('model') || (err.message || '').toLowerCase().includes('model');
        const shouldRetry = modelsToTry.length > 1 && (response.status === 404 || response.status === 500 || modelErr);
        lastErr = err;
        if (shouldRetry) {
          continue;
        }
        params.onError?.(err);
        throw err;
      }

      // If we reach here, response is ok
      break;
    }

    if (!response || !response.ok) {
      const err = (lastErr as Error) || new Error('Apex request failed and no fallback models succeeded.');
      params.onError?.(err as Error);
      throw err;
    }
  } catch (err) {
    params.onError?.(err as Error);
    throw err;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    const err = new Error('No response body for streaming');
    params.onError?.(err);
    throw err;
  }

  // Record start time for response time calculation
  const startTime = Date.now();

  const decoder = new TextDecoder('utf-8');
  let accumulated = '';
  let buffer = '';
  const blockTypes = new Map<number, string>();
  let inputTokens: number | undefined;
  let outputTokens: number | undefined;
  let reasoningTokens: number | undefined;
  let thinkingCharsAccumulated = 0;

  // Timeout for each stream chunk read - prevents indefinite hangs on network issues
  const STREAM_CHUNK_TIMEOUT_MS = 30000; // 30 seconds per chunk

  try {
    while (true) {
      // Check abort signal before each read to allow cancellation
      if (params.signal?.aborted) {
        console.log('[VideoScript] Abort signal detected - stopping stream');
        break;
      }

      // Add timeout to each read operation to prevent indefinite hangs
      const readPromise = reader.read();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Stream chunk timeout - no data received for 30 seconds')), STREAM_CHUNK_TIMEOUT_MS);
      });

      let readResult: ReadableStreamReadResult<Uint8Array>;
      try {
        readResult = await Promise.race([readPromise, timeoutPromise]);
      } catch (timeoutError) {
        console.error('[VideoScript] Stream chunk timeout - aborting read loop');
        throw timeoutError;
      }

      const { done, value } = readResult;
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith(':')) continue;

        if (trimmed.startsWith('event: ')) {
          continue;
        }

        if (!trimmed.startsWith('data: ')) continue;

        try {
          const jsonStr = trimmed.slice(6);
          const parsed = JSON.parse(jsonStr);

          // ----- Anthropic native SSE format -----
          if (parsed.type === 'content_block_start') {
            if (typeof parsed.index === 'number' && parsed.content_block?.type) {
              blockTypes.set(parsed.index, parsed.content_block.type);
            }
          } else if (parsed.type === 'content_block_delta') {
            const blockType = typeof parsed.index === 'number' ? blockTypes.get(parsed.index) : undefined;
            // Handle thinking_delta for extended thinking content
            if (parsed.delta?.type === 'thinking_delta' || blockType === 'thinking') {
              const thinkingText = parsed.delta?.thinking || parsed.delta?.text || '';
              if (thinkingText) {
                thinkingCharsAccumulated += thinkingText.length;
              }
              continue; // Hide thinking content from user-facing stream
            }
            if (parsed.delta?.type !== 'text_delta') continue;
            const delta = parsed.delta.text;
            if (delta) {
              accumulated += delta;
              params.onChunk?.(delta, accumulated);
            }
          } else if (parsed.type === 'content_block_stop') {
            if (typeof parsed.index === 'number') {
              blockTypes.delete(parsed.index);
            }
          } else if (parsed.type === 'message_start' && parsed.message?.usage) {
            inputTokens = parsed.message.usage.input_tokens;
            reasoningTokens =
              parsed.message.usage.reasoning_tokens ??
              parsed.message.usage.thinking_tokens ??
              reasoningTokens;
          } else if (parsed.type === 'message_delta' && parsed.usage) {
            outputTokens = parsed.usage.output_tokens;
            if (parsed.usage.reasoning_tokens !== undefined) {
              reasoningTokens = parsed.usage.reasoning_tokens;
            } else if (parsed.usage.thinking_tokens !== undefined) {
              reasoningTokens = parsed.usage.thinking_tokens;
            }
          } else if (parsed.type === 'message_stop') {
            break;
          } else if (parsed.type === 'error') {
            const errMsg = parsed.error?.message || 'Unknown streaming error';
            const err = new Error(errMsg);
            params.onError?.(err);
            throw err;
          }
          // ----- OpenAI / OpenRouter SSE format fallback -----
          // The anthropicProxy routes through OpenRouter which returns OpenAI-format chunks:
          //   data: {"choices":[{"delta":{"content":"..."}}]}
          // Without this fallback, streaming video script generation silently accumulates
          // nothing because no Anthropic event types match, resulting in an empty script.
          else if (parsed.choices && Array.isArray(parsed.choices)) {
            const choice = parsed.choices[0] as Record<string, any>;
            if (choice?.delta) {
              // Handle reasoning/thinking content from OpenRouter
              if (choice.delta.reasoning || choice.delta.reasoning_content) {
                const reasoningText = choice.delta.reasoning || choice.delta.reasoning_content || '';
                if (reasoningText) {
                  thinkingCharsAccumulated += reasoningText.length;
                }
                // Don't add to accumulated — reasoning is hidden from user
              }
              // Handle regular text content
              const content = (choice.delta.content as string) || '';
              if (content) {
                accumulated += content;
                params.onChunk?.(content, accumulated);
              }
            }
            // Capture model info
            if (parsed.model && !modelUsed) {
              modelUsed = parsed.model as string;
            }
            // Capture usage from OpenAI format
            if (parsed.usage) {
              inputTokens = parsed.usage.prompt_tokens ?? inputTokens;
              outputTokens = parsed.usage.completion_tokens ?? outputTokens;
              if (parsed.usage.reasoning_tokens !== undefined) {
                reasoningTokens = parsed.usage.reasoning_tokens;
              }
            }
            // Detect end of stream
            if (choice?.finish_reason === 'stop' || choice?.finish_reason === 'end_turn') {
              break;
            }
          }
        } catch (parseErr) {
          if (String(parseErr).includes('Unknown streaming error')) {
            throw parseErr;
          }
        }
      }
    }

    // Process any remaining buffer content
    if (buffer.trim() && buffer.trim().startsWith('data: ')) {
      try {
        const jsonStr = buffer.trim().slice(6);
        const parsed = JSON.parse(jsonStr);
        // Anthropic format
        if (parsed.type === 'content_block_delta' && parsed.delta?.type === 'text_delta') {
          const delta = parsed.delta.text;
          if (delta) {
            accumulated += delta;
            params.onChunk?.(delta, accumulated);
          }
        }
        if (parsed.type === 'message_delta' && parsed.usage) {
          outputTokens = parsed.usage.output_tokens;
        }
        // OpenAI/OpenRouter format
        if (parsed.choices && Array.isArray(parsed.choices)) {
          const choice = parsed.choices[0] as Record<string, any>;
          const content = choice?.delta?.content;
          if (content) {
            accumulated += content;
            params.onChunk?.(content, accumulated);
          }
          if (parsed.usage) {
            inputTokens = parsed.usage.prompt_tokens ?? inputTokens;
            outputTokens = parsed.usage.completion_tokens ?? outputTokens;
          }
        }
      } catch {
        // Ignore
      }
    }
  } finally {
    reader.releaseLock();
  }

  const responseTimeMs = Date.now() - startTime;
  const finalText = accumulated.trim();

  // If no reasoning tokens from usage, estimate from thinking content
  const estimatedThinkingTokens = thinkingCharsAccumulated > 0
    ? Math.ceil(thinkingCharsAccumulated / 4)
    : undefined;
  const finalReasoningTokens = reasoningTokens ?? estimatedThinkingTokens;

  // Build token usage metadata
    // FALLBACK: If no usage data was received in the stream (provider didn't include it),
  // estimate from the accumulated text to avoid recording $0 usage.
  if (inputTokens === undefined && outputTokens === undefined && finalText.length > 0) {
    console.warn('[VideoScript] No usage data in stream — estimating from content length');
    outputTokens = Math.ceil(finalText.length / 4);
    inputTokens = Math.ceil(outputTokens * 0.3);
  }

  const tokenUsage: TokenUsageMetadata = {
    inputTokens,
    outputTokens,
    reasoningTokens: finalReasoningTokens,
    totalTokens: (inputTokens || 0) + (outputTokens || 0) + (finalReasoningTokens || 0) || undefined,
    model: modelUsed,
    provider: 'claude',
    responseTimeMs,
    timestamp: new Date(),
  };

  params.onComplete?.(finalText, tokenUsage);

  console.log(`[VideoScript] Stage 2 Generation complete - ${finalText.length} chars, ${responseTimeMs}ms`);

  return { text: finalText, tokenUsage };
}
